import type { ReactNode } from 'react';

/**
 * Los componentes que se repiten, de docs/DISENO.md.
 *
 * Viven juntos a proposito: son cinco cosas pequenas y tenerlas en un archivo
 * hace mas facil mantenerlas coherentes que repartirlas en cinco.
 */

/*
 * Ambar para lo que esta pendiente, rojo solo para lo que esta mal de verdad.
 *
 * ENTREGADA va en relleno solido y no en fondo tenue: es el unico estado que
 * cierra algo, y de un vistazo tiene que distinguirse de RECIBIDA, que es del
 * mismo verde pero significa lo contrario —acaba de llegar, no ha pasado nada—.
 *
 * ENTREGADA_PARCIAL es el primer sitio del sistema donde el rojo esta
 * justificado: un faltante es exactamente lo que docs/DISENO.md reserva para
 * ese color, y es la fila que hay que ver desde la puerta.
 */
const ESTILO_ESTADO: Record<string, string> = {
  RECIBIDA: 'bg-acento-tenue text-acento',
  REQUIERE_REVISION: 'bg-aviso-tenue text-aviso',
  ENTREGADA: 'bg-acento text-white',
  ENTREGADA_PARCIAL: 'bg-alerta-tenue text-alerta',
  ANULADA: 'bg-neutro-tenue text-neutro',
};

const TEXTO_ESTADO: Record<string, string> = {
  REQUIERE_REVISION: 'REVISAR',
  ENTREGADA_PARCIAL: 'PARCIAL',
};

/** El estado se lee por forma y color, no solo por texto. */
export function Pill({ estado }: { estado: string }): ReactNode {
  const texto = TEXTO_ESTADO[estado] ?? estado;
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[0.7rem] font-semibold tracking-wide ${
        ESTILO_ESTADO[estado] ?? 'bg-neutro-tenue text-neutro'
      }`}
    >
      {texto}
    </span>
  );
}

export function Card({
  children,
  titulo,
  className = '',
}: {
  children: ReactNode;
  titulo?: string;
  className?: string;
}): ReactNode {
  return (
    <section className={`rounded-xl border border-borde bg-superficie p-4 shadow-suave ${className}`}>
      {titulo && <h2 className="mb-3 text-base font-semibold text-marino">{titulo}</h2>}
      {children}
    </section>
  );
}

/** Un numero grande con su etiqueta. Nunca un numero sin contexto. */
export function Tile({
  etiqueta,
  valor,
  pie,
}: {
  etiqueta: string;
  valor: string;
  pie?: string;
}): ReactNode {
  return (
    // La franja de arriba es de la marca, no un estado: todas las cifras la llevan igual.
    <div className="relative overflow-hidden rounded-xl border border-borde bg-superficie p-4 shadow-suave">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-acento via-oliva to-lima" />
      <div className="text-xs font-medium uppercase tracking-wide text-tenue">{etiqueta}</div>
      <div className="cifra mt-1 text-2xl font-semibold text-marino !text-left">{valor}</div>
      {pie && <div className="mt-1 text-xs text-tenue">{pie}</div>}
    </div>
  );
}

export function Boton({
  children,
  onClick,
  tipo = 'principal',
  deshabilitado = false,
  submit = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: 'principal' | 'secundario' | 'peligro';
  deshabilitado?: boolean;
  submit?: boolean;
}): ReactNode {
  const estilos = {
    principal: 'bg-acento text-white shadow-sm hover:bg-marino',
    secundario: 'border border-borde bg-superficie text-marino hover:border-acento/40 hover:bg-acento-tenue',
    peligro: 'border border-alerta bg-superficie text-alerta hover:bg-alerta-tenue',
  }[tipo];

  return (
    <button
      type={submit ? 'submit' : 'button'}
      onClick={onClick}
      disabled={deshabilitado}
      /*
       * `min-h-[44px]` en estrecho y el tamano de siempre a partir de `sm`.
       *
       * Un dedo no es un puntero (`docs/DISENO.md`): 32 px de alto se falla.
       * En computador no cambia nada, que es como Pedro lo queria.
       */
      className={`min-h-[44px] rounded-lg px-3.5 py-1.5 text-base font-medium transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:text-sm ${estilos}`}
    >
      {children}
    </button>
  );
}

/**
 * Cuando el sistema no sabe algo, lo dice. No un espacio en blanco, no un
 * valor inventado: el dato crudo y la palabra «no identificada».
 */
export function NoSe({ crudo, que }: { crudo: string; que: string }): ReactNode {
  return (
    <span className="text-tenue" title={`${que} no identificada: ${crudo}`}>
      <span className="font-mono text-xs">{crudo}</span>
      <span className="ml-1 text-xs italic">no identificada</span>
    </span>
  );
}

export function Cargando({ que }: { que: string }): ReactNode {
  return (
    <p className="flex items-center justify-center gap-2 p-8 text-tenue">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-acento/20 border-t-acento" aria-hidden="true" />
      Cargando {que}…
    </p>
  );
}

export function Aviso({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="rounded-lg border border-aviso/30 border-l-4 border-l-aviso bg-aviso-tenue px-3 py-2 text-sm text-aviso">
      {children}
    </div>
  );
}

export function Error_({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="rounded-lg border border-alerta/30 border-l-4 border-l-alerta bg-alerta-tenue px-3 py-2 text-sm text-alerta">
      {children}
    </div>
  );
}

/** Las tablas anchas llevan su propio desplazamiento: el cuerpo nunca se corre. */
/**
 * Una tabla ancha dentro de su propio desplazamiento horizontal.
 *
 * En un telefono no cabe y **no debe caber**: encogerla hasta que quepa la
 * vuelve ilegible, que es peor que tener que arrastrarla. Las pantallas que se
 * usan de pie —F4 y F9— no usan tablas por eso mismo; estas son de consulta, y
 * eso se hace sentado o con el telefono en horizontal.
 *
 * Lo que si hacia falta: **decir que se desplaza**. Un corte limpio en el borde
 * derecho parece el final de la tabla, y nadie arrastra lo que no sabe que
 * puede arrastrar. Por eso el aviso debajo en estrecho, y por eso
 * `overscroll-x-contain`: arrastrar la tabla hasta el final no arrastra ademas
 * la pagina de atras.
 */
export function Tabla({ children }: { children: ReactNode }): ReactNode {
  return (
    <div>
      <div className="overflow-x-auto overscroll-x-contain rounded-xl border border-borde bg-superficie shadow-suave">
        <table className="w-full min-w-[720px] border-collapse text-sm">{children}</table>
      </div>
      <p className="mt-1 text-xs text-tenue sm:hidden">Desliza la tabla para ver el resto →</p>
    </div>
  );
}
